import React, { useState } from 'react';
import type { OverlayTemplateResponse, Tendv2ContentItem } from '../../api/tendApi';
import type { Logo } from '../../api/logoApi';

import OverlayPreview from '../tend/OverlayPreview';

/**
 * One "default template" slot: label + badge, a preview of the template in
 * effect (or a dashed "Set default" tile), and a picker dropdown that saves
 * on pick. Extracted from TemplateDefaultsTab (Design ▾ Default Templates)
 * so the per-tactic slot in Editorial ▾ Tactics / the tactic modal is the
 * same control. Presentational: the host owns the data and the resolution;
 * this renders whatever `template` it is handed (own or inherited).
 */

export interface TemplateDefaultSlotBadge {
  /** e.g. "BRAND", "GLOBAL", "INHERITED", "INHERITED · Post default" */
  text: string;
  /** Muted tone for an inherited value; solid for the scope's own choice. */
  inherited: boolean;
}

export interface TemplateDefaultSlotNoneOption {
  /** First picker row, e.g. "None — use Post default (Clean)". */
  label: string;
  onClear: () => void | Promise<void>;
}

export interface TemplateDefaultSlotProps {
  /** Slot title; '\n' breaks lines. */
  label: string;
  /** The template to preview: the scope's own choice or the inherited one. */
  template: OverlayTemplateResponse | null;
  badge?: TemplateDefaultSlotBadge | null;
  /** Under-preview text while a template shows (defaults to its name). */
  caption?: string;
  /** Under-tile text while nothing resolves. */
  emptyHint?: string;
  /** Picker options; hidden templates are filtered here. */
  templates: OverlayTemplateResponse[];
  /** The scope's OWN pick (highlighted row); null highlights the None row. */
  selectedTemplateId: number | null;
  onSelect: (templateId: number) => void | Promise<void>;
  noneOption?: TemplateDefaultSlotNoneOption | null;
  previewContentItem: Tendv2ContentItem;
  brandLogo: Logo | null;
  brandName: string;
  previewFillColor: string;
  width?: number;
  /** Read-only: the picker never opens. */
  disabled?: boolean;
  /** Controlled picker state (a host keeping one picker open at a time). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hook for tests / hosts; not rendered as text. */
  'data-testid'?: string;
}

const TemplateDefaultSlot: React.FC<TemplateDefaultSlotProps> = ({
  label,
  template,
  badge,
  caption,
  emptyHint,
  templates,
  selectedTemplateId,
  onSelect,
  noneOption,
  previewContentItem,
  brandLogo,
  brandName,
  previewFillColor,
  width = 180,
  disabled = false,
  open,
  onOpenChange,
  'data-testid': testId,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const toggle = () => {
    if (disabled) return;
    setOpen(!isOpen);
  };

  const visibleTemplates = templates.filter((t) => !t.is_hidden);

  return (
    <div className="relative inline-block" style={{ width }} data-testid={testId}>
      {/* Header with inline dropdown trigger; a long badge ("INHERITED · Post
          default") wraps under the label instead of overflowing a narrow slot */}
      <div className="flex flex-wrap content-start items-start gap-x-1.5 gap-y-0.5 mb-1.5 h-10">
        <button
          type="button"
          onClick={toggle}
          disabled={disabled}
          aria-expanded={isOpen}
          className="flex items-start gap-1 text-sm font-medium text-white hover:text-blue-400 transition-colors text-left whitespace-pre-line leading-tight disabled:hover:text-white disabled:cursor-default"
        >
          {label}
          {!disabled && (
            <svg className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
        {badge && (
          <span
            className={`text-[9px] font-medium px-1 py-0.5 rounded mt-0.5 leading-tight max-w-full truncate ${
              badge.inherited ? 'bg-gray-600 text-gray-300' : 'bg-blue-600 text-white'
            }`}
            data-inherited={badge.inherited ? 'true' : 'false'}
          >
            {badge.text}
          </span>
        )}
      </div>

      {/* Preview */}
      <div className="relative">
        {template ? (
          <div
            className={`rounded-lg overflow-hidden border border-gray-700 transition-colors ${disabled ? '' : 'cursor-pointer hover:border-gray-500'}`}
            onClick={toggle}
          >
            <OverlayPreview
              contentItem={previewContentItem}
              config={template.config}
              brandLogo={brandLogo}
              brandName={brandName}
              onConfigChange={() => {}}
              size="small"
              controlsEnabled={false}
              fallbackBgColor={previewFillColor}
            />
          </div>
        ) : (
          <div
            className={`aspect-[4/5] bg-gray-800/50 rounded-lg border border-dashed border-gray-600 flex items-center justify-center transition-colors ${disabled ? '' : 'cursor-pointer hover:border-gray-500'}`}
            onClick={toggle}
          >
            <div className="text-center text-gray-500">
              <svg className="w-6 h-6 mx-auto mb-1 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-[10px]">{disabled ? 'No default' : 'Set default'}</span>
            </div>
          </div>
        )}
        <div className="mt-1 text-xs text-gray-400 truncate" title={template ? (caption ?? template.name ?? 'Unnamed') : emptyHint}>
          {template ? (caption ?? template.name ?? 'Unnamed') : emptyHint}
        </div>

        {/* Picker dropdown — overlays the preview */}
        {isOpen && (
          <div className="absolute z-50 inset-x-0 top-0 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-72 overflow-auto" role="listbox">
            {noneOption && (
              <button
                type="button"
                role="option"
                aria-selected={selectedTemplateId === null}
                onClick={async () => { await noneOption.onClear(); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 text-left transition-colors border-b border-gray-700 ${
                  selectedTemplateId === null ? 'bg-blue-600/20' : 'hover:bg-gray-700'
                }`}
              >
                <div className="w-10 h-10 rounded flex-shrink-0 border border-dashed border-gray-600 flex items-center justify-center text-gray-500 text-base">
                  –
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-white truncate">{noneOption.label}</div>
                  <div className="text-[9px] text-gray-400">Inherit</div>
                </div>
              </button>
            )}
            {visibleTemplates.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={selectedTemplateId === t.id}
                onClick={async () => { await onSelect(t.id); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 text-left transition-colors ${
                  selectedTemplateId === t.id ? 'bg-blue-600/20' : 'hover:bg-gray-700'
                }`}
              >
                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-700">
                  <OverlayPreview
                    contentItem={previewContentItem}
                    config={t.config}
                    brandLogo={null}
                    brandName={brandName}
                    onConfigChange={() => {}}
                    size="small"
                    controlsEnabled={false}
                    fallbackBgColor={previewFillColor}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-white truncate">{t.name || 'Unnamed'}</div>
                  <div className="text-[9px] text-gray-400">{t.is_global ? 'Global' : 'Brand'}</div>
                </div>
              </button>
            ))}
            {visibleTemplates.length === 0 && (
              <div className="text-center py-3 text-gray-500 text-xs">No templates available</div>
            )}
          </div>
        )}
      </div>

      {/* Click-away for the picker (in-tree, so host popovers stay open) */}
      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="template-slot-clickaway" />
      )}
    </div>
  );
};

export default TemplateDefaultSlot;
